import { useQuery } from "@tanstack/react-query";
import api from "../config/axios";
import { Reservation } from "../features/reservations/types/Reservation";
import { logger } from "../utils/logger";
import { useAuth } from "../features/auth/contexts/AuthContext";
import { formatDateKey } from "../features/shared/utils/dateUtils";

// Tipos para los filtros de reservas
export interface ReservationsFilters {
  page?: number;
  per_page?: number;
  rango?: string;
  cliente_id?: number;
  search?: string;
  estado?: string;
  origen?: string;
  saldo?: string;
  departamento?: string;
  movimiento?: string;
  orden?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  calendar?: boolean;
}

// Tipos para la respuesta paginada
export interface ReservationsResponse {
  reservas: Reservation[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  summary?: {
    con_saldo: number;
    saldadas: number;
    a_favor: number;
    entradas_hoy: number;
    salidas_hoy: number;
    pendiente_total: number;
  };
}

// Función para obtener reservas
export const fetchReservations = async (
  filters: ReservationsFilters = {}
): Promise<ReservationsResponse> => {
  // Construir query params
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.per_page) params.append("per_page", filters.per_page.toString());
  if (filters.rango) params.append("rango", filters.rango);
  if (filters.cliente_id)
    params.append("cliente_id", filters.cliente_id.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.estado) params.append("estado", filters.estado);
  if (filters.origen) params.append("origen", filters.origen);
  if (filters.saldo) params.append("saldo", filters.saldo);
  if (filters.departamento) params.append("departamento", filters.departamento);
  if (filters.movimiento) params.append("movimiento", filters.movimiento);
  if (filters.orden) params.append("orden", filters.orden);
  if (filters.fecha_desde) params.append("fecha_desde", filters.fecha_desde);
  if (filters.fecha_hasta) params.append("fecha_hasta", filters.fecha_hasta);
  if (filters.calendar) params.append("calendar", "true");

  const url = `/reservas?${params.toString()}`;
  logger.info("🔍 fetchReservations URL:", url);
  logger.info("🔍 fetchReservations filters:", filters);

  const response = await api.get<ReservationsResponse>(url);

  // Manejar tanto la nueva estructura como la antigua para compatibilidad
  const reservasData = response.data.reservas || response.data;
  const paginationData = response.data.pagination || {
    page: 1,
    per_page: 25,
    total: reservasData.length,
    pages: 1,
    has_next: false,
    has_prev: false,
  };

  return {
    reservas: Array.isArray(reservasData) ? reservasData : [],
    pagination: paginationData,
    summary: response.data.summary,
  };
};

// Hook principal para obtener reservas
export const useReservations = (filters: ReservationsFilters = {}) => {
  return useQuery({
    queryKey: ["reservations", filters],
    queryFn: () => fetchReservations(filters),
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// Hook para obtener reservas de un cliente específico
export const useClientReservations = (clientId: number | null) => {
  return useQuery({
    queryKey: ["client-reservations", clientId],
    queryFn: () =>
      fetchReservations({ cliente_id: clientId || undefined, rango: "todas" }),
    enabled: !!clientId, // Solo ejecutar si hay un clientId
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });
};

// Hook para obtener reservas del calendario (todas las reservas sin paginación)
export const useCalendarReservations = () => {
  // Solo ejecutar la query si el usuario está autenticado
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ["calendar-reservations"],
    queryFn: () => fetchReservations({ calendar: true }),
    enabled: isAuthenticated, // 🔥 Solo ejecutar si el usuario está autenticado
    staleTime: 10 * 60 * 1000, // 10 minutos (aumentado para reducir refetching)
    gcTime: 15 * 60 * 1000, // 15 minutos
    retry: 2,
    refetchOnWindowFocus: false, // 🔥 DESHABILITADO para evitar spam
    refetchOnReconnect: true,
  });
};

// 🔥 NUEVO: Hook para obtener reservas de un mes específico del calendario
export const useMonthCalendarReservations = (year: number, month: number, enabled = true) => {
  // Calcular fechas del mes con buffer de 1 mes antes y después
  const startDate = new Date(year, month - 2, 1); // 1 mes antes
  const endDate = new Date(year, month + 1, 0); // Último día del mes siguiente
  
  const filters: ReservationsFilters = {
    calendar: true,
    fecha_desde: formatDateKey(startDate),
    fecha_hasta: formatDateKey(endDate),
  };

  return useQuery({
    queryKey: ["month-calendar-reservations", year, month],
    queryFn: () => fetchReservations(filters),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    enabled: enabled && !isNaN(year) && !isNaN(month) && year > 0 && month > 0 && month <= 12,
  });
};

// Función para obtener una reserva específica por ID
const fetchReservationById = async (id: number): Promise<Reservation> => {
  const response = await api.get<Reservation>(`/reservas/${id}`);
  return response.data;
};

// Hook para obtener una reserva específica por ID
export const useReservationById = (id: number | null) => {
  return useQuery({
    queryKey: ["reservation", id],
    queryFn: () => fetchReservationById(id!),
    enabled: !!id, // Solo ejecutar si hay un ID
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });
};

// Función para obtener información de un departamento
const fetchDepartmentInfo = async (
  departamento: string
): Promise<Record<string, unknown>> => {
  const response = await api.get(`/departamentos/${departamento}`);
  return response.data;
};

// Hook para obtener información de un departamento
export const useDepartmentInfo = (departamento: string | null) => {
  return useQuery({
    queryKey: ["department-info", departamento],
    queryFn: () => fetchDepartmentInfo(departamento!),
    enabled: !!departamento, // Solo ejecutar si hay un departamento
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
    retry: 2,
  });
};

// 🔥 MEJORADO: Hook unificado para obtener reservas con filtros de fecha
export const useReservationsByDateRange = (
  startDate: Date,
  endDate: Date,
  additionalFilters: Partial<ReservationsFilters> = {}
) => {
  const startMonth = formatDateKey(startDate).slice(0, 7); // YYYY-MM local
  const endMonth = formatDateKey(endDate).slice(0, 7); // YYYY-MM local

  return useQuery({
    queryKey: [
      "reservations-by-date-range",
      startMonth,
      endMonth,
      additionalFilters,
    ],
    queryFn: () =>
      fetchReservations({
        fecha_desde: formatDateKey(startDate),
        fecha_hasta: formatDateKey(endDate),
        ...additionalFilters, // 🔥 NUEVO: Permitir filtros adicionales
      }),
    enabled: !!startDate && !!endDate,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
  });
};

// Hook para obtener reservas por mes
export const useReservationsByMonth = (year: number, month: number) => {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);

  return useQuery({
    queryKey: ["reservations-by-month", year, month],
    queryFn: () =>
      fetchReservations({
        fecha_desde: formatDateKey(startDate),
        fecha_hasta: formatDateKey(endDate),
      }),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });
};
